import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronDown } from 'react-icons/fa6';
import { CSVFileEntry } from '../../../../../types/common';
import InfoTooltip from '@/shared/components/forms/InfoTooltip';

interface ConfigSectionProps {
  entry: CSVFileEntry;
  isExpanded: boolean;
  onToggle: () => void;
  onInputChange: (field: keyof CSVFileEntry, value: string) => void;
  onRadioChange: (value: 'ja' | 'nein') => void;
  isOutputData?: boolean;
}

export const ConfigSection: React.FC<ConfigSectionProps> = ({
  entry,
  isExpanded,
  onToggle,
  onInputChange,
  onRadioChange,
  isOutputData = false
}) => {
  const { t } = useTranslation('training');

  const inputClasses = 'w-full bg-card border border-input rounded-lg px-2.5 py-1.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring transition-shadow duration-150 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';
  const selectClasses = 'w-full bg-card border border-input rounded-lg px-2.5 py-1.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring transition-shadow duration-150';
  const labelClasses = 'text-[10px] text-muted-foreground uppercase tracking-wide block mb-1';
  const readOnlyClasses = 'w-full bg-muted border border-border rounded-lg px-2.5 py-1.5 text-xs text-muted-foreground';

  // Output data: always Zeithorizont (no dropdown), Input data: depends on selection
  const isZeithorizont = isOutputData || entry.datenform === 'Historische Daten';

  return (
    <div className="rounded-lg border border-border overflow-visible">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3.5 py-2.5 bg-muted/80 hover:bg-muted transition-colors duration-150"
      >
        <h3 className="text-xs font-medium text-foreground uppercase tracking-wide">
          {t('csvFileUploader.sections.config')}
        </h3>
        <FaChevronDown className={`w-2.5 h-2.5 text-muted-foreground transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
      </button>

      {isExpanded && (
        <div className="px-3.5 py-3 bg-card border-t border-border">
          <div className="grid grid-cols-2 gap-3">
            {/* Datenform dropdown — only for input data */}
            {!isOutputData && (
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <label
                    htmlFor={`csv-datenform-${entry.id}`}
                    className={labelClasses}
                    style={{ marginBottom: 0 }}
                  >
                    {t('csvFileUploader.headers.datenform')}
                  </label>
                  <InfoTooltip text={t('tooltips.config.datenform')} width="w-64" align="start" />
                </div>
                <select
                  id={`csv-datenform-${entry.id}`}
                  value={entry.datenform}
                  onChange={(e) => onInputChange('datenform', e.target.value)}
                  className={selectClasses}
                >
                  <option value="">{t('csvFileUploader.options.pleaseSelect')}</option>
                  <option value="Historische Daten">{t('csvFileUploader.options.datenform.historischeDaten')}</option>
                  <option value="Aktueller Wert">{t('csvFileUploader.options.datenform.aktuellerWert')}</option>
                </select>
              </div>
            )}

            {/* Fields below: always visible for output, conditional for input */}
            {isZeithorizont && (
              <>
                {/* Mittelwertbildung */}
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <label className={labelClasses} style={{ marginBottom: 0 }}>
                      {t('csvFileUploader.headers.mittelwertbildungÜberDenZeithorizont')}
                    </label>
                    <InfoTooltip text={t('tooltips.config.mittelwertbildung')} width="w-64" align="end" />
                  </div>
                  <div className="flex items-center gap-4 mt-0.5">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name={`mittelwertbildungÜberDenZeithorizont-${entry.id}`}
                        value="ja"
                        checked={entry.mittelwertbildungÜberDenZeithorizont === 'ja'}
                        onChange={() => onRadioChange('ja')}
                        className="accent-primary"
                      />
                      <span className="text-xs text-foreground">{t('csvFileUploader.options.yes')}</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name={`mittelwertbildungÜberDenZeithorizont-${entry.id}`}
                        value="nein"
                        checked={entry.mittelwertbildungÜberDenZeithorizont === 'nein'}
                        onChange={() => onRadioChange('nein')}
                        className="accent-primary"
                      />
                      <span className="text-xs text-foreground">{t('csvFileUploader.options.no')}</span>
                    </label>
                  </div>
                </div>

                {/* Zeithorizont Start */}
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <label
                      htmlFor={`csv-zeithorizontStart-${entry.id}`}
                      className={labelClasses}
                      style={{ marginBottom: 0 }}
                    >
                      {t('csvFileUploader.headers.zeithorizontStart')}
                    </label>
                    <InfoTooltip text={t('tooltips.config.zeithorizontStart')} width="w-64" align="start" />
                  </div>
                  <input
                    id={`csv-zeithorizontStart-${entry.id}`}
                    type="number"
                    value={entry.zeithorizontStart}
                    onChange={(e) => onInputChange('zeithorizontStart', e.target.value)}
                    className={inputClasses}
                  />
                </div>

                {/* Zeithorizont End */}
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <label
                      htmlFor={`csv-zeithorizontEnd-${entry.id}`}
                      className={labelClasses}
                      style={{ marginBottom: 0 }}
                    >
                      {t('csvFileUploader.headers.zeithorizontEnd')}
                    </label>
                    <InfoTooltip text={t('tooltips.config.zeithorizontEnde')} width="w-64" align="end" />
                  </div>
                  <input
                    id={`csv-zeithorizontEnd-${entry.id}`}
                    type="number"
                    value={entry.zeithorizontEnd}
                    onChange={(e) => onInputChange('zeithorizontEnd', e.target.value)}
                    className={inputClasses}
                  />
                </div>
              </>
            )}

            {/* Zeitschrittweite & Offset — only for Zeithorizont, not for Aktueller Wert */}
            {isZeithorizont && (
              <>
                <div>
                  <label className={labelClasses}>
                    {t('csvFileUploader.headers.zeitschrittweiteTransferiertenDaten')}
                  </label>
                  <div className={readOnlyClasses}>
                    {entry.zeitschrittweiteTransferiertenDaten || '-'}
                  </div>
                </div>

                <div>
                  <label className={labelClasses}>
                    {t('csvFileUploader.headers.offsetTransferiertenDaten')}
                  </label>
                  <div className={readOnlyClasses}>
                    {entry.offsetTransferiertenDaten || '-'}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfigSection;
